import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { BookOpen, Calculator, FlaskConical, Moon, Globe, LogOut, Camera, User, BarChart3 } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ImageSolverModal } from '@/components/ImageSolverModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getSubjectIcon = (icon) => {
  const icons = {
    'book-open': <BookOpen />,
    'calculator': <Calculator />,
    'flask': <FlaskConical />,
    'moon': <Moon />,
    'globe': <Globe />,
    'book': <BookOpen />,
  };
  return icons[icon] || <BookOpen />;
};

const SubjectsPage = () => {
  const { gradeId, semesterId } = useParams();
  const navigate = useNavigate();
  const [subjects, setSubjects] = useState([]);
  const [gradeName, setGradeName] = useState('');
  const [loading, setLoading] = useState(true);
  const [solverOpen, setSolverOpen] = useState(false);
  const { user, logout, getAuthHeaders } = useAuth();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  useEffect(() => {
    const fetchSubjects = async () => {
      try {
        const semesterParam = semesterId ? `?semester=${semesterId}` : '';
        const [subjectsRes, gradesRes] = await Promise.all([
          axios.get(`${API_URL}/api/grades/${gradeId}/subjects${semesterParam}`, { headers: getAuthHeaders() }),
          axios.get(`${API_URL}/api/grades`, { headers: getAuthHeaders() })
        ]);
        setSubjects(subjectsRes.data);
        const currentGrade = gradesRes.data.find(g => g.id === gradeId);
        if (currentGrade) setGradeName(currentGrade.name_ar);
      } catch (error) {
        toast.error('فشل في تحميل المواد');
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    fetchSubjects();
  }, [gradeId, semesterId, getAuthHeaders]);

  const semLabel = semesterId === '1' ? 'الفصل الأول' : semesterId === '2' ? 'الفصل الثاني' : '';
  const titleSpan = `${gradeName}${semLabel ? ` (${semLabel})` : ''}`;

  return (
    <div className="sbpage" dir="rtl" data-testid="subjects-page">
      {/* Header */}
      <header className="nav" data-testid="main-header">
        <Link to="/grades" className="brand" data-testid="logo-link">RasheedGPT</Link>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="who" data-testid="user-menu-trigger">
              <i aria-hidden="true">{user?.name?.charAt(0)}</i>
              <span className="hidden sm:inline">{user?.name}</span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-48 bg-white border-black/10 text-[#1f1a33]">
            <div className="px-3 py-2 border-b border-black/5">
              <p className="font-semibold text-[#1f1a33]">{user?.name}</p>
              <p className="text-xs text-[#625c78]">{user?.role === 'admin' ? 'مدير' : user?.role === 'student' ? 'طالب' : 'ولي أمر'}</p>
            </div>
            {user?.role === 'admin' && (
              <DropdownMenuItem asChild className="text-[#4a3aa8] hover:text-[#4a3aa8] hover:bg-black/5 cursor-pointer">
                <Link to="/admin"><BarChart3 className="w-4 h-4 me-2" />لوحة التحكم</Link>
              </DropdownMenuItem>
            )}
            <DropdownMenuItem asChild className="text-[#1f1a33]/80 hover:text-[#1f1a33] hover:bg-black/5 cursor-pointer">
              <Link to="/grades">الصفوف</Link>
            </DropdownMenuItem>
            {user?.role !== 'admin' && (
              <DropdownMenuItem asChild className="text-[#1f1a33]/80 hover:text-[#1f1a33] hover:bg-black/5 cursor-pointer">
                <Link to="/profile" data-testid="profile-link"><User className="w-4 h-4 me-2" />الملف الشخصي</Link>
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={() => setSolverOpen(true)} className="text-[#1f1a33]/80 hover:text-[#1f1a33] hover:bg-black/5 cursor-pointer" data-testid="solve-question-btn">
              <Camera className="w-4 h-4 me-2" />حل سؤال
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleLogout} className="text-red-500 hover:text-red-600 hover:bg-black/5 cursor-pointer" data-testid="logout-button">
              <LogOut className="w-4 h-4 me-2" />تسجيل الخروج
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </header>

      <div className="rings" aria-hidden="true"><i></i><i></i><i></i></div>

      <main>
        <button className="back" onClick={() => navigate(`/grades/${gradeId}`)} data-testid="back-to-semesters">
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
          العودة للفصول الدراسية
        </button>

        <div className="hd">
          <span className="badge">
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 1l11 11-11 11L1 12z" /></svg>
            المواد الدراسية
          </span>
          <h1>المواد الدراسية - <span>{titleSpan}</span></h1>
          <div className="hadith">
            <small>قال رسول الله صلى الله عليه وسلم</small>
            <p>"من سلك طريقاً يلتمس فيه علماً سهّل الله له به طريقاً إلى الجنة"</p>
          </div>
        </div>

        {loading ? (
          <nav className="gr">
            {Array.from({ length: 4 }).map((_, i) => (
              <div className="sb" key={i} style={{ '--i': i, opacity: 0.5 }}>
                <div className="ic"><BookOpen /></div>
                <div className="tx"><h2>…</h2></div>
              </div>
            ))}
          </nav>
        ) : subjects.length === 0 ? (
          <nav className="gr">
            <div className="empty" data-testid="no-subjects">
              <h3 style={{ margin: '0 0 6px', fontWeight: 700, color: 'var(--ink)' }}>لا توجد مواد حالياً</h3>
              <p style={{ margin: 0 }}>سيتم إضافة المواد الدراسية قريباً</p>
            </div>
          </nav>
        ) : (
          <nav className="gr" aria-label="المواد الدراسية" data-testid="subjects-grid">
            {subjects.map((subject, i) => (
              <Link to={`/subjects/${subject.id}/lessons`} key={subject.id} className="sb" style={{ '--i': i }} data-testid={`subject-card-${subject.id}`}>
                <div className="ic">{getSubjectIcon(subject.icon)}</div>
                <div className="tx">
                  <h2>{subject.name_ar}</h2>
                  <span>{subject.name_en}</span>
                </div>
                <svg className="ch" viewBox="0 0 24 24" aria-hidden="true"><path d="M15 5l-7 7 7 7" /></svg>
              </Link>
            ))}
          </nav>
        )}
      </main>

      <ImageSolverModal open={solverOpen} onOpenChange={setSolverOpen} />
    </div>
  );
};

export default SubjectsPage;
